/**
 * super_admin-only toggle to un-mask hashed user tokens in per-user tables.
 *
 * Flipping this to ON writes to admin_audit_log via the backend (every read
 * that includes ?reveal=true produces an audit entry in _common.assert_can_reveal).
 * A confirmation modal warns the operator before first unmasking.
 *
 * Default off. Session-scoped (no persistence across logout).
 */
import React, { useState } from 'react';
import { Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { cn } from '../../../../lib/cn';

export interface RevealIdentifiersToggleProps {
  revealed: boolean;
  onChange: (next: boolean) => void;
  /** Is the currently logged-in admin a super_admin? */
  isSuperAdmin: boolean;
  className?: string;
}

export const RevealIdentifiersToggle: React.FC<RevealIdentifiersToggleProps> = ({
  revealed,
  onChange,
  isSuperAdmin,
  className,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!isSuperAdmin) {
    return (
      <div className={cn('inline-flex items-center gap-1.5 text-[11px] text-gray-400', className)}>
        <EyeOff className="h-3.5 w-3.5" aria-hidden />
        Anonymized
      </div>
    );
  }

  const handleClick = () => {
    if (!revealed) {
      setConfirmOpen(true);
    } else {
      onChange(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors',
          revealed
            ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100'
            : 'bg-gray-50 text-gray-600 ring-1 ring-gray-200 hover:bg-gray-100',
          className,
        )}
        aria-pressed={revealed}
        title={revealed ? 'Hide user identifiers' : 'Reveal user identifiers (audited)'}
      >
        {revealed ? (
          <>
            <Eye className="h-3.5 w-3.5" />
            Identifiers visible
          </>
        ) : (
          <>
            <EyeOff className="h-3.5 w-3.5" />
            Anonymized
          </>
        )}
      </button>
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Reveal user identifiers?</h3>
                <p className="mt-1 text-xs text-gray-600">
                  Unmasking replaces hashed tokens with real user IDs. Every request made while
                  revealed is recorded to{' '}
                  <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-700">
                    admin_audit_log
                  </code>{' '}
                  with your admin ID, IP, and the request path.
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="rounded-md px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange(true);
                  setConfirmOpen(false);
                }}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Reveal and audit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
